/**
 * MoreMenu Component
 * 
 * A dropdown menu component for file/item actions with Apple-inspired design.
 * Uses React Portal to render outside parent container, avoiding overflow issues.
 * Implements touch target sizing for accessibility (min 44px touch targets).
 */

import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../../utils/cn';
import { Download, FolderOpen, Trash2, Link2, Edit3, Shield, Eye } from 'lucide-react';

interface MoreMenuProps {
  onRename?: () => void;
  onMove?: () => void;
  onDownload?: () => void;
  onDelete?: () => void;
  onPermissions?: () => void;
  onCopyLink?: () => void;
  onPreview?: () => void;
  onClose?: () => void;
  /** 触发按钮的位置信息，用于定位菜单 */
  triggerRect?: DOMRect;
  labels?: {
    rename?: string;
    moveTo?: string;
    download?: string;
    delete?: string;
    permissions?: string;
    copyLink?: string;
    preview?: string;
  };
}

interface MenuItemProps {
  onClick: () => void;
  label: string;
  icon?: React.ReactNode;
  variant?: 'default' | 'destructive';
}

const MenuItem: React.FC<MenuItemProps> = ({ onClick, label, icon, variant = 'default' }) => (
  <button
    className={cn(
      'w-full text-left flex items-center gap-3',
      'min-h-[44px] px-4 py-2.5',
      'text-sm font-medium',
      'transition-colors duration-150',
      variant === 'destructive'
        ? 'text-red-600 hover:bg-red-50'
        : 'text-slate-700 hover:bg-slate-50'
    )}
    onClick={(e) => {
      e.stopPropagation();
      onClick();
    }}
  >
    {icon && (
      <span className={cn(
        'flex-shrink-0',
        variant === 'destructive' ? 'text-red-500' : 'text-slate-400'
      )}>
        {icon}
      </span>
    )}
    {label}
  </button>
);

const MoreMenu: React.FC<MoreMenuProps> = ({ 
  onRename, 
  onMove, 
  onDownload, 
  onDelete, 
  onPermissions,
  onCopyLink,
  onPreview,
  onClose, 
  triggerRect,
  labels 
}) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [isPositioned, setIsPositioned] = useState(false);
  const [mounted, setMounted] = useState(false);
  const portalContainerRef = useRef<HTMLDivElement | null>(null);

  // 安全创建和销毁 portal 容器
  useEffect(() => {
    // 创建专门的 portal 容器
    const container = document.createElement('div');
    container.id = `more-menu-portal-${Date.now()}`;
    document.body.appendChild(container);
    portalContainerRef.current = container;
    setMounted(true);

    return () => {
      setMounted(false);
      // 延迟移除，确保 React 有时间清理
      setTimeout(() => {
        if (container.parentNode) {
          container.parentNode.removeChild(container);
        }
      }, 0);
    };
  }, []);

  const t = {
    rename: labels?.rename ?? '重命名',
    moveTo: labels?.moveTo ?? '移动到',
    download: labels?.download ?? '下载',
    delete: labels?.delete ?? '删除',
    permissions: labels?.permissions ?? '权限设置',
    copyLink: labels?.copyLink ?? '复制链接',
    preview: labels?.preview ?? '预览',
  };

  // 计算菜单位置 - 使用 requestAnimationFrame 确保 DOM 渲染完成
  useEffect(() => {
    if (!triggerRect) {
      console.log('[MoreMenu] 没有 triggerRect，跳过位置计算');
      return;
    }

    // 使用 requestAnimationFrame 确保 DOM 已渲染
    const calcPosition = () => {
      if (!menuRef.current) {
        console.log('[MoreMenu] menuRef.current 为 null，等待下一帧');
        requestAnimationFrame(calcPosition);
        return;
      }

      const menuRect = menuRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      
      console.log('[MoreMenu] 计算位置:', { 
        triggerRect: { top: triggerRect.top, left: triggerRect.left, bottom: triggerRect.bottom, right: triggerRect.right },
        menuRect: { width: menuRect.width, height: menuRect.height },
        viewport: { width: viewportWidth, height: viewportHeight }
      });
      
      let top = triggerRect.bottom + 4;
      let left = triggerRect.right - menuRect.width;

      // 如果菜单超出底部，则显示在按钮上方
      if (top + menuRect.height > viewportHeight - 20) {
        top = triggerRect.top - menuRect.height - 4;
      }

      // 如果菜单超出左边，则靠左显示
      if (left < 20) {
        left = 20;
      }

      // 如果菜单超出右边
      if (left + menuRect.width > viewportWidth - 20) {
        left = viewportWidth - menuRect.width - 20;
      }

      console.log('[MoreMenu] 最终位置:', { top, left });
      setPosition({ top, left });
      setIsPositioned(true);
    };

    // 立即尝试计算，如果 DOM 还没准备好则等待下一帧
    requestAnimationFrame(calcPosition);
  }, [triggerRect]);

  // 点击外部关闭菜单
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose?.();
      }
    };

    // 延迟添加事件监听，避免立即触发
    const timer = setTimeout(() => {
      document.addEventListener('click', handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [onClose]);

  // ESC 键关闭
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose?.();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const hasMainActions = onPreview || onDownload || onCopyLink;
  const hasEditActions = onRename || onMove;
  const hasSettings = onPermissions;
  const hasDestructive = onDelete;

  const menuContent = (
    <div 
      ref={menuRef}
      className={cn(
        'fixed z-[9999]',
        'bg-white',
        'border border-slate-200',
        'rounded-xl',
        'shadow-xl',
        'w-52 py-1',
        'overflow-hidden',
        isPositioned ? 'opacity-100' : 'opacity-0',
        'transition-opacity duration-100'
      )}
      style={{ top: position.top, left: position.left }}
      role="menu"
      aria-orientation="vertical"
      onClick={(e) => e.stopPropagation()}
    >
      {/* 主要操作 */}
      {onPreview && (
        <MenuItem 
          onClick={() => { onPreview(); onClose?.(); }} 
          label={t.preview}
          icon={<Eye size={16} />}
        />
      )}
      {onDownload && (
        <MenuItem 
          onClick={() => { 
            console.log('MoreMenu: onDownload clicked');
            onDownload(); 
            onClose?.(); 
          }} 
          label={t.download}
          icon={<Download size={16} />}
        />
      )}
      {onCopyLink && (
        <MenuItem 
          onClick={() => { onCopyLink(); onClose?.(); }} 
          label={t.copyLink}
          icon={<Link2 size={16} />}
        />
      )}
      
      {/* 分隔线 */}
      {hasMainActions && hasEditActions && (
        <div className="my-1 border-t border-slate-100" />
      )}
      
      {/* 编辑操作 */}
      {onRename && (
        <MenuItem 
          onClick={() => { onRename(); onClose?.(); }} 
          label={t.rename}
          icon={<Edit3 size={16} />}
        />
      )}
      {onMove && (
        <MenuItem 
          onClick={() => { onMove(); onClose?.(); }} 
          label={t.moveTo}
          icon={<FolderOpen size={16} />}
        />
      )}
      
      {/* 分隔线 */}
      {(hasMainActions || hasEditActions) && hasSettings && (
        <div className="my-1 border-t border-slate-100" />
      )}
      
      {/* 设置 */}
      {onPermissions && (
        <MenuItem 
          onClick={() => { onPermissions(); onClose?.(); }} 
          label={t.permissions}
          icon={<Shield size={16} />}
        />
      )}
      
      {/* 分隔线 */}
      {hasDestructive && (hasMainActions || hasEditActions || hasSettings) && (
        <div className="my-1 border-t border-slate-100" />
      )}
      
      {/* 危险操作 */}
      {onDelete && (
        <MenuItem 
          onClick={() => { onDelete(); onClose?.(); }} 
          label={t.delete}
          icon={<Trash2 size={16} />}
          variant="destructive"
        />
      )}
    </div>
  );
  // 使用 Portal 渲染到专用容器，避免被父容器遮挡和 DOM 错误
  if (!mounted || !portalContainerRef.current) return null;
  return createPortal(menuContent, portalContainerRef.current);
};

export default MoreMenu;
